/**
 * The pure commit: the one function the store (2.8) and the client's
 * optimistic draw share. Given the head version, the version the ops were
 * made on, and what changed since, it answers whether the batch lands, and
 * with what receipt and problems.
 *
 *   (a) apply the ops to the head; a batch that no longer applies is `moved`
 *       when it was made on an older version, else `refused`
 *   (b) made on an older version: any path it touched meeting a path touched
 *       since is `moved` (J2.5)
 *   (c) the same draft is `unchanged`; a record built with entries
 *       (`heldDirections`) given another direction is `refused`
 *   (d) a snapshot over `STUDIO_LIMITS.snapshotBytes` is `too_big`
 *   (e) a problem the head did not have refuses the batch (J2.3): `moved`
 *       when rebased (the newer version is why it does not fit), else
 *       `refused`
 *   (f) else `ok`, with the receipt's lines and the draft's problems
 *
 * A restore is never refused (J2.6): the person chose that version, and a
 * problem it carries becomes a gap.
 */
import { applyStudioOps, type StudioOpProblem } from './apply';
import { STUDIO_LIMITS, type StudioDraft, type StudioRecord } from './draft';
import { newStudioProblems, studioProblems, type StudioProblem } from './judge';
import type { StudioOp } from './ops';
import { studioOverlaps, studioReceiptLines, type StudioReceiptLine } from './receipt';

export type StudioCommitInput = {
  head: StudioDraft;
  headVersion: number;
  headProblems: readonly StudioProblem[];
  baseVersion: number;
  /** Every path the versions after `baseVersion` touched, up to the head. */
  touchedSince: readonly string[];
  ops: readonly StudioOp[];
  /**
   * Which way money goes on each record that is built and already holds
   * entries, by record key (an edit of a workspace in use, 9.16): a batch that
   * changes one of these is refused `DIRECTION_HELD`. Money direction is set
   * while setting up; once a record has entries it stays (Devendra, 16 Oct).
   * Absent on a setup, where any direction may change.
   */
  heldDirections?: StudioHeldDirections;
};

/** A built record's direction that cannot change, with the record's name for the words. */
export type StudioHeldDirections = Readonly<Record<string, { readonly direction: StudioRecord['direction']; readonly name: string }>>;

const DIRECTION_WORDS: Readonly<Record<StudioRecord['direction'], string>> = {
  in: 'money in',
  out: 'money out',
  both: 'money in and out',
  count: 'a count',
  none: 'no money',
};

/** Why a held record's direction stays: the words a refusal and the compiler say. */
export const heldDirectionWords = (name: string, direction: StudioRecord['direction']): string =>
  `${name} already holds entries, so which way its money goes cannot change now: it stays ${DIRECTION_WORDS[direction]}. Add a new record for the other way instead.`;

/** The held records a draft gives another direction than the head had, each with the last op of the batch that set it. */
const heldDirectionProblems = (
  head: StudioDraft,
  draft: StudioDraft,
  ops: readonly StudioOp[],
  held: StudioHeldDirections,
): StudioOpProblem[] =>
  Object.entries(held).flatMap(([key, kept]) => {
    const now = draft.records.find((one) => one.key === key);
    const was = head.records.find((one) => one.key === key);
    if (now === undefined || now.direction === kept.direction || now.direction === was?.direction) return [];
    let op: number | null = null;
    ops.forEach((one, at) => {
      if ((one.op === 'update_record' && one.record === key && one.direction !== undefined) || (one.op === 'add_record' && one.key === key)) op = at;
    });
    return [{ code: 'DIRECTION_HELD', op, message: heldDirectionWords(now.name, kept.direction), part: `record:${key}` }];
  });

export type StudioCommitResult =
  | { kind: 'ok'; draft: StudioDraft; touched: string[]; lines: StudioReceiptLine[]; problems: StudioProblem[]; rebased: boolean }
  | { kind: 'unchanged' }
  | { kind: 'refused'; problems: StudioOpProblem[] }
  | { kind: 'moved'; conflicts: string[]; problems: StudioOpProblem[] }
  | { kind: 'too_big'; bytes: number };

/** The UTF-8 length of a string, without an encoder. */
export const utf8Bytes = (text: string): number => {
  let bytes = 0;
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && index + 1 < text.length) {
      bytes += 4;
      index += 1;
    } else bytes += 3;
  }
  return bytes;
};

const asOpProblem = (problem: StudioProblem): StudioOpProblem => ({
  code: problem.code,
  op: null,
  message: problem.message,
  ...(problem.part === null ? {} : { part: problem.part }),
});

export const studioCommit = (input: StudioCommitInput): StudioCommitResult => {
  const rebased = input.baseVersion < input.headVersion;
  const applied = applyStudioOps(input.head, input.ops);
  if (!applied.ok) return rebased ? { kind: 'moved', conflicts: [], problems: applied.problems } : { kind: 'refused', problems: applied.problems };

  if (rebased) {
    const conflicts = studioOverlaps(applied.touched, input.touchedSince);
    if (conflicts.length > 0) return { kind: 'moved', conflicts, problems: [] };
  }

  const json = JSON.stringify(applied.draft);
  if (json === JSON.stringify(input.head)) return { kind: 'unchanged' };
  const held = input.heldDirections === undefined ? [] : heldDirectionProblems(input.head, applied.draft, input.ops, input.heldDirections);
  if (held.length > 0) return { kind: 'refused', problems: held };
  const bytes = utf8Bytes(json);
  if (bytes > STUDIO_LIMITS.snapshotBytes) return { kind: 'too_big', bytes };

  const problems = studioProblems(applied.draft);
  const brought = newStudioProblems(input.headProblems, problems);
  if (brought.length > 0) {
    const said = brought.map(asOpProblem);
    return rebased ? { kind: 'moved', conflicts: [], problems: said } : { kind: 'refused', problems: said };
  }

  return { kind: 'ok', draft: applied.draft, touched: applied.touched, lines: studioReceiptLines(input.head, applied.draft), problems, rebased };
};

/** Back to an earlier version: never refused, touches everything, and its receipt starts with the version it went back to. */
export const studioRestore = (
  head: StudioDraft,
  target: StudioDraft,
  version: number,
): { draft: StudioDraft; touched: ['*']; lines: StudioReceiptLine[]; problems: StudioProblem[] } => ({
  draft: structuredClone(target),
  touched: ['*'],
  lines: [{ op: 'restored', version }, ...studioReceiptLines(head, target)],
  problems: studioProblems(target),
});
